// frontend/src/pages/Notifications.js — Session 20
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';
import theme from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Config types notifications ────────────────────────────────────────────────
const TYPE_CONFIG = {
  // Admin reçoit
  profil_modifie:    { icon:'👤', label:'Profil modifié',        color:'#3498db', bg:'#ebf5ff' },
  nouveau_client:    { icon:'🆕', label:'Nouveau client',         color:'#27ae60', bg:'#e8f8f0' },
  nouveau_devis:     { icon:'📄', label:'Nouveau devis',          color:'#8e44ad', bg:'#f5eef8' },
  devis_cree:        { icon:'📄', label:'Devis créé',             color:'#8e44ad', bg:'#f5eef8' },
  devis_accepte:     { icon:'✅', label:'Devis accepté',          color:'#27ae60', bg:'#e8f8f0' },
  devis_refuse:      { icon:'❌', label:'Devis refusé',           color:'#e74c3c', bg:'#fff5f5' },
  demande_produit:   { icon:'🛒', label:'Demande produit',        color:'#e67e22', bg:'#fef9f0' },
  document_expire:   { icon:'⏰', label:'Document expirant',      color:'#e67e22', bg:'#fef9f0' },
  paiement_recu:     { icon:'💰', label:'Paiement reçu',          color:'#27ae60', bg:'#e8f8f0' },
  profil_admin_maj:  { icon:'🔧', label:'Profil mis à jour',      color:'#4f6d89', bg:'#f0f4f8' },
  // Client reçoit
  devis_emis:        { icon:'📬', label:'Devis disponible',       color:'#3498db', bg:'#ebf5ff' },
  devis_modifie:     { icon:'📝', label:'Devis modifié',          color:'#8e44ad', bg:'#f5eef8' },
  facture_emise:     { icon:'📑', label:'Facture disponible',     color:'#2c3e50', bg:'#eaf0f6' },
  facture_relance:   { icon:'⚠️', label:'Rappel paiement',        color:'#e74c3c', bg:'#fff5f5' },
  email_modifie:     { icon:'📧', label:'Email modifié',          color:'#e74c3c', bg:'#fff5f5' },
  mdp_reinitialise:  { icon:'🔑', label:'Mot de passe réinitialisé', color:'#e67e22', bg:'#fef9f0' },
  avenant:           { icon:'📋', label:'Avenant',                color:'#6a1b9a', bg:'#f3e5f5' },
  note_credit:       { icon:'💳', label:'Note de crédit',         color:'#00796b', bg:'#e0f2f1' },
  // Défaut
  default:           { icon:'🔔', label:'Notification',           color:'#666',    bg:'#f5f5f5' },
};

function getConfig(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.default;
}

function tempsRelatif(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const j = Math.floor(diff / 86400000);
  if (m < 1)  return 'À l\'instant';
  if (m < 60) return `Il y a ${m} min`;
  if (h < 24) return `Il y a ${h}h`;
  if (j < 7)  return `Il y a ${j} jour${j>1?'s':''}`;
  return new Date(dateStr).toLocaleDateString('fr-BE', { day:'2-digit', month:'short', year:'numeric' });
}

const Notifications = () => {
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const config    = { headers: { Authorization: `Bearer ${token}` } };

  const [notifications, setNotifications] = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [filtre,        setFiltre]        = useState('tous'); // tous | non_lu | lu
  const [typeFiltre,    setTypeFiltre]    = useState('');
  const [offset,        setOffset]        = useState(0);
  const LIMIT = 20;

  const fetchNotifs = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: LIMIT,
        offset: reset ? 0 : offset,
        ...(filtre !== 'tous' ? { statut: filtre } : {}),
      });
      const res = await axios.get(`${API_URL}/notifications?${params}`, config);
      const data = res.data.data || [];
      const filtered = typeFiltre ? data.filter(n => n.type === typeFiltre) : data;
      setNotifications(reset ? filtered : prev => [...prev, ...filtered]);
      setTotal(res.data.total || 0);
      if (reset) setOffset(LIMIT);
      else setOffset(o => o + LIMIT);
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  }, [token, filtre, typeFiltre, offset]);

  useEffect(() => { fetchNotifs(true); }, [filtre, typeFiltre, token]);

  const marquerLu = async (id) => {
    try {
      await axios.patch(`${API_URL}/notifications/${id}/lu`, {}, config);
      setNotifications(ns => ns.map(n => n.id_notification === id ? { ...n, statut: 'lu' } : n));
    } catch { /* silencieux */ }
  };

  const toutLire = async () => {
    try {
      await axios.patch(`${API_URL}/notifications/tout-lire`, {}, config);
      setNotifications(ns => ns.map(n => ({ ...n, statut: 'lu' })));
    } catch { /* silencieux */ }
  };

  const supprimer = async (id) => {
    try {
      await axios.delete(`${API_URL}/notifications/${id}`, config);
      setNotifications(ns => ns.filter(n => n.id_notification !== id));
    } catch { /* silencieux */ }
  };

  const supprimerLues = async () => {
    if (!window.confirm('Supprimer toutes les notifications lues ?')) return;
    try {
      await axios.delete(`${API_URL}/notifications/tout`, config);
      fetchNotifs(true);
    } catch { /* silencieux */ }
  };

  const nbNonLues = notifications.filter(n => n.statut === 'non_lu').length;

  return (
    <div style={{ padding: isMobile ? '14px 12px' : '24px', maxWidth: '860px', margin: '0 auto' }}>

      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: '#2c3e50', fontWeight: '700' }}>
            🔔 Notifications
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#666' }}>
            {total} notification{total !== 1 ? 's' : ''}
            {nbNonLues > 0 && <span style={{ color: '#e74c3c', fontWeight: '600' }}> · {nbNonLues} non lue{nbNonLues > 1 ? 's' : ''}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {nbNonLues > 0 && (
            <button onClick={toutLire} style={{ padding: '8px 14px', background: '#4f6d89', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              ✅ Tout marquer lu
            </button>
          )}
          <button onClick={supprimerLues} style={{ padding: '8px 14px', background: '#fff5f5', color: '#e74c3c', border: '1px solid #fed7d7', borderRadius: '7px', cursor: 'pointer', fontSize: '13px' }}>
            🗑️ Vider les lues
          </button>
        </div>
      </div>

      {/* Filtres statut */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[['tous','Toutes'],['non_lu','Non lues'],['lu','Lues']].map(([v,l]) => (
          <button key={v} onClick={() => { setFiltre(v); setOffset(0); }} style={{
            padding: '7px 14px', border: 'none', borderRadius: '20px', cursor: 'pointer',
            fontSize: '13px', fontWeight: '600',
            background: filtre === v ? theme.primary : '#f0f0f0',
            color: filtre === v ? 'white' : '#555',
          }}>{l}</button>
        ))}
      </div>

      {/* Filtre par type */}
      <div style={{ marginBottom: '16px' }}>
        <select value={typeFiltre} onChange={e => setTypeFiltre(e.target.value)}
          style={{ padding: '7px 12px', border: `1px solid ${theme.border}`, borderRadius: '7px', fontSize: '13px', background: 'white', color: '#555',
            width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
          <option value="">Tous les types</option>
          {Object.entries(TYPE_CONFIG).filter(([k]) => k !== 'default').map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
      </div>

      {/* Liste */}
      {loading && notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>⏳ Chargement…</div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#999', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Aucune notification</p>
          <p style={{ margin: '6px 0 0', fontSize: '14px' }}>Vous êtes à jour !</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {notifications.map(notif => {
            const cfg = getConfig(notif.type);
            const nonLu = notif.statut === 'non_lu';
            return (
              <div key={notif.id_notification} style={{
                background: nonLu ? 'white' : '#fafafa',
                border: `1px solid ${nonLu ? cfg.color + '44' : '#e8e8e8'}`,
                borderLeft: `4px solid ${nonLu ? cfg.color : '#ddd'}`,
                borderRadius: '10px', padding: '14px 16px',
                display: 'flex', gap: '14px', alignItems: 'flex-start',
                boxShadow: nonLu ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                transition: 'opacity 0.2s',
              }}>
                {/* Icône */}
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  background: cfg.bg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '18px', flexShrink: 0,
                }}>
                  {cfg.icon}
                </div>

                {/* Contenu */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: '700', color: cfg.color,
                      background: cfg.bg, padding: '2px 8px', borderRadius: '10px',
                    }}>
                      {cfg.label}
                    </span>
                    <span style={{ fontSize: '12px', color: '#aaa', flexShrink: 0 }}>
                      {tempsRelatif(notif.date_creation)}
                    </span>
                  </div>
                  <p style={{
                    margin: '6px 0 4px', fontSize: '14px',
                    color: nonLu ? '#2c3e50' : '#666',
                    fontWeight: nonLu ? '500' : '400',
                    lineHeight: '1.4',
                  }}>
                    {notif.message}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {notif.numero_devis && (
                      <Link to="/devis" style={{ fontSize: '12px', color: theme.primary, textDecoration: 'none', fontWeight: '600' }}>
                        📄 {notif.numero_devis}
                      </Link>
                    )}
                    {notif.numero_facture && (
                      <Link to="/factures" style={{ fontSize: '12px', color: theme.primary, textDecoration: 'none', fontWeight: '600' }}>
                        📑 {notif.numero_facture}
                      </Link>
                    )}
                    {notif.lien_url && (
                      <Link to={notif.lien_url} style={{ fontSize: '12px', color: '#666', textDecoration: 'none' }}>
                        → Voir
                      </Link>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {nonLu && (
                    <button onClick={() => marquerLu(notif.id_notification)}
                      title="Marquer comme lu"
                      style={{ padding: '5px 9px', background: '#e8f8f0', color: '#27ae60', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      ✓
                    </button>
                  )}
                  <button onClick={() => supprimer(notif.id_notification)}
                    title="Supprimer"
                    style={{ padding: '5px 9px', background: '#fff5f5', color: '#e74c3c', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                    ✕
                  </button>
                </div>
              </div>
            );
          })}

          {/* Charger plus */}
          {notifications.length < total && (
            <button onClick={() => fetchNotifs(false)} disabled={loading} style={{
              padding: '12px', background: 'white', border: `1px solid ${theme.border}`,
              borderRadius: '8px', cursor: 'pointer', color: '#666', fontSize: '14px',
              marginTop: '4px',
            }}>
              {loading ? '⏳ Chargement…' : `Afficher plus (${total - notifications.length} restantes)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
